import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { TrustBand } from "@/components/site/TrustBand";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { aeoGeo } from "@/lib/seo/schema";
import {
  getAnswersByLanguage,
  getAnswerSlug,
  toFaqJsonLd,
} from "@/lib/aeo/answers";

const QUESTIONS = getAnswersByLanguage("en");

export const Route = createFileRoute("/en_/questions")({
  head: () => ({
    meta: [
      { title: "Questions about video, subscriptions and pricing | Fenny" },
      {
        name: "description",
        content:
          "Clear, fact-checked answers to the most common questions about video production, video subscriptions and what video costs in Sweden.",
      },
      { property: "og:title", content: "Video questions & answers | Fenny" },
      {
        property: "og:description",
        content:
          "Fact-checked answers on video pricing, how a video subscription works and the difference between Pulse and Prime.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/en/questions" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/en/questions" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/questions", name: "Questions about video, subscriptions and pricing | Fenny", description: "Clear, fact-checked answers to the most common questions about video production, video subscriptions and what video costs in Sweden."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(toFaqJsonLd(QUESTIONS.map((q) => q.id))),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: QUESTIONS.map((q, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: q.question,
            url: `/en/questions/${getAnswerSlug(q)}`,
          })),
        }),
      },
    ],
  }),
  component: EnQuestionsHubPage,
});

function EnQuestionsHubPage() {
  return (
    <div>
      <EditorialHero
        eyebrow="Questions & answers"
        title="Straight answers about video."
        highlight={[2, 3]}
        tone="mint"
        lede="The questions we hear most about video production, video subscriptions and what video actually costs — answered briefly and fact-checked."
      />

      <Section className="bg-surface/40">
        <EyebrowHeading eyebrow="All questions" title="Pick a question" align="center" />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {QUESTIONS.map((q) => (
            <Link
              key={q.id}
              to="/en/questions/$slug"
              params={{ slug: getAnswerSlug(q) }}
              className="group flex flex-col rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-mint/30"
            >
              <h2 className="text-xl font-semibold leading-tight">{q.question}</h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground/80">
                {q.shortAnswer}
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm text-foreground/80 group-hover:text-foreground">
                Read the full answer
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <SignatureCta
        eyebrow="Can't find the answer?"
        title="Ask us directly."
        body="30 minutes, free of charge. We give you a straight answer on what video would cost and which model fits — Pulse, Prime or a hybrid."
        tone="mint"
      />

      <TrustBand ctaLocation="en_questions_hub_trust_band" />
    </div>
  );
}
